import { StandardEvents } from '@shopify/events';

import { requestKlaviyo, newsletterPayload, restockPayload } from './dome-klaviyo.js';

export class DomeRestockSignup extends HTMLElement {
  #controller;
  #generation = 0;
  #variant = null;
  #submitting = false;
  #subscribedEmail = '';
  #dialog;
  #form;
  #trigger;
  #submit;
  #status;
  #email;

  connectedCallback() {
    this.#controller?.abort();
    this.#controller = new AbortController();
    const { signal } = this.#controller;
    this.#dialog = this.querySelector('dialog');
    this.#form = this.querySelector('form');
    this.#trigger = this.querySelector('[data-restock-open]');
    this.#submit = this.querySelector('[type="submit"]');
    this.#status = this.querySelector('[data-restock-status]');
    this.#email = this.querySelector('[type="email"]');
    if (!this.#dialog || !this.#form || !this.#trigger || !this.#submit || !this.#status || !this.#email) return;

    this.setVariant(this.dataset.variantId ? {
      id: this.dataset.variantId,
      title: this.dataset.variantTitle,
      available: this.dataset.available === 'true',
    } : null);

    this.#trigger.addEventListener('click', () => this.open(), { signal });
    this.querySelector('[data-restock-close]')?.addEventListener('click', () => this.#dialog.close(), { signal });
    this.#dialog.addEventListener('click', (event) => {
      if (event.target !== this.#dialog) return;
      const bounds = this.#dialog.getBoundingClientRect();
      if (event.clientX < bounds.left || event.clientX > bounds.right || event.clientY < bounds.top || event.clientY > bounds.bottom) {
        this.#dialog.close();
      }
    }, { signal });
    this.#dialog.addEventListener('close', () => {
      if (!this.hidden) this.#trigger.focus();
    }, { signal });
    this.#form.addEventListener('submit', (event) => {
      event.preventDefault();
      event.stopPropagation();
      void this.submit();
    }, { signal });
    this.closest('.shopify-section, dialog, product-card')?.addEventListener(StandardEvents.productSelect, this.#onSelection, { signal });
    this.dataset.ready = '';
  }

  disconnectedCallback() {
    this.#controller?.abort();
    this.#generation++;
    if (this.#dialog?.open) this.#dialog.close();
  }

  setVariant(variant) {
    this.#variant = variant;
    this.hidden = !variant?.id || variant.available !== false;
    this.dataset.variantId = variant?.id ? String(variant.id) : '';
    this.dataset.available = String(variant?.available !== false);
    this.#trigger.disabled = this.hidden;
    this.querySelector('[data-restock-size]').textContent = variant?.title ?? '';
    this.#status.textContent = '';
    this.#status.dataset.state = '';
    this.#form.hidden = false;
    this.#submit.disabled = this.#submitting;
  }

  #onSelection = async (event) => {
    const sourceCard = event.target?.closest('product-card');
    if (sourceCard && !sourceCard.contains(this)) return;
    const generation = ++this.#generation;
    this.setVariant(null);
    if (this.#dialog.open) this.#dialog.close();
    try {
      const { detail } = await event.promise;
      if (!this.isConnected || generation !== this.#generation) return;
      if (!detail?.newProduct && String(detail?.productId) !== this.dataset.productId) return;
      this.setVariant(detail.resource);
    } catch {
      if (generation === this.#generation) this.setVariant(null);
    }
  };

  open() {
    if (this.hidden || !this.#variant?.id || this.#variant.available !== false) return;
    this.#dialog.showModal();
    this.querySelector('h2')?.focus();
  }

  async submit() {
    if (this.#submitting || this.hidden || !this.#variant?.id || !this.#form.reportValidity()) return;
    const variantId = String(this.#variant.id);
    const generation = this.#generation;
    const email = this.#email.value.trim();
    if (!email) return;
    const alertPayload = restockPayload(email, variantId);
    this.#submitting = true;
    this.#submit.disabled = true;
    this.#status.dataset.state = 'pending';
    this.#status.textContent = this.dataset.saving;
    try {
      if (this.#subscribedEmail !== email) {
        await requestKlaviyo('subscriptions', newsletterPayload(email));
        this.#subscribedEmail = email;
      }
      await requestKlaviyo('back-in-stock-subscriptions', alertPayload);
      if (!this.isConnected || generation !== this.#generation) return;
      this.#form.hidden = true;
      this.#status.dataset.state = 'success';
      this.#status.textContent = this.dataset.success;
      this.#status.focus();
    } catch {
      if (!this.isConnected || generation !== this.#generation) return;
      this.#status.dataset.state = 'error';
      this.#status.textContent = this.#subscribedEmail === email ? this.dataset.partialError : this.dataset.error;
    } finally {
      this.#submitting = false;
      this.#submit.disabled = false;
    }
  }
}

if (!customElements.get('dome-restock-signup')) customElements.define('dome-restock-signup', DomeRestockSignup);
